import { useEffect, useState } from "react";
import { useLang } from "@/contexts/LanguageContext";
import { api } from "@/lib/api";
import { Quote, Star } from "lucide-react";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from "@/components/ui/carousel";

export default function Testimonials({ propertyId = null }) {
  const { t } = useLang();
  const [items, setItems] = useState([]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const url = propertyId ? `/testimonials?property_id=${propertyId}` : "/testimonials";
        const { data } = await api.get(url);
        if (!cancelled) setItems(data || []);
      } catch {
        if (!cancelled) setItems([]);
      }
    })();
    return () => { cancelled = true; };
  }, [propertyId]);

  if (!items.length) return null;

  return (
    <section id="reseñas" data-testid="testimonials-section" className="bg-stone-100 py-24 md:py-40">
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 mb-16 items-end">
          <div className="lg:col-span-7">
            <p className="text-eyebrow text-[#E07A5F] mb-4">Reseñas de huéspedes</p>
            <h2 className="font-serif-display text-4xl md:text-5xl lg:text-6xl leading-[1.05] tracking-tight text-stone-900">
              Lo que dicen quienes ya nos visitaron.
            </h2>
          </div>
          <div className="lg:col-span-5">
            <p className="text-stone-700 text-lg leading-relaxed">
              Testimonios reales de huéspedes que han pasado por nuestras villas en Barra de Galindo.
            </p>
          </div>
        </div>

        <Carousel opts={{ align: "start", loop: items.length > 1 }} className="w-full">
          <CarouselContent className="-ml-4 md:-ml-6">
            {items.map((tst) => (
              <CarouselItem
                key={tst.id}
                data-testid={`testimonial-${tst.id}`}
                className="pl-4 md:pl-6 basis-full md:basis-1/2 lg:basis-1/3"
              >
                <article className="bg-white border border-stone-200 p-7 md:p-8 h-full flex flex-col">
                  <div className="flex items-start justify-between mb-4">
                    <Quote className="w-7 h-7 text-[#E07A5F]" />
                    {tst.source && (
                      <span
                        className="text-[10px] uppercase tracking-[0.22em] px-2 py-1 border"
                        style={{
                          color: tst.source === "Google" ? "#1A73E8" : "#FF385C",
                          borderColor: tst.source === "Google" ? "#1A73E8" : "#FF385C",
                        }}
                        title={`Reseña real verificada de ${tst.source}`}
                      >
                        <i
                          className={`fa-brands fa-${tst.source === "Google" ? "google" : "airbnb"} mr-1`}
                        />
                        {tst.source}
                      </span>
                    )}
                  </div>
                  <div className="flex gap-1 mb-4">
                    {Array.from({ length: tst.rating || 5 }).map((_, i) => (
                      <Star key={i} className="w-4 h-4 text-[#F2CC8F] fill-[#F2CC8F]" />
                    ))}
                  </div>
                  <p className="text-stone-700 leading-relaxed mb-6 flex-1">"{tst.text}"</p>
                  <div className="border-t border-stone-200 pt-4">
                    <p className="font-serif-display text-lg text-stone-900">{tst.name}</p>
                    <p className="text-xs uppercase tracking-wider text-stone-500 mt-1">
                      {[tst.location, tst.date].filter(Boolean).join(" · ")}
                    </p>
                  </div>
                </article>
              </CarouselItem>
            ))}
          </CarouselContent>
          {items.length > 1 && (
            <>
              <CarouselPrevious className="left-0 -translate-x-2 md:-translate-x-4 rounded-none bg-stone-900 border-stone-900 text-stone-50 hover:bg-[#E07A5F] hover:border-[#E07A5F] hover:text-white" />
              <CarouselNext className="right-0 translate-x-2 md:translate-x-4 rounded-none bg-stone-900 border-stone-900 text-stone-50 hover:bg-[#E07A5F] hover:border-[#E07A5F] hover:text-white" />
            </>
          )}
        </Carousel>
      </div>
    </section>
  );
}
